import { UserCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import { EMPTY_VALUE } from '../../../utils/formatCurrency';
import { formatDateTime } from '../../../utils/formatDateTime';
import { PlanReadOnlyField } from '../../molecules/PlanReadOnlyField';
import { PlanSectionCard } from '../../molecules/PlanSectionCard';

/** Props de `ParcelAuditSection`. */
export interface ParcelAuditSectionProps {
  /** Autoria da criação. */
  readonly createdBy?: string | null | undefined;
  /** Data-hora da criação, no formato ISO do backend. */
  readonly createdAt?: string | null | undefined;
  /** Autoria da última alteração. */
  readonly updatedBy?: string | null | undefined;
  /** Data-hora da última alteração, no formato ISO do backend. */
  readonly updatedAt?: string | null | undefined;
}

/**
 * Bloco de auditoria da parcela — quem criou e quem alterou, com as duas datas.
 *
 * Os quatro campos vêm de `GET /parcels/{parcelId}` (`userCreation`,
 * `creationDate`, `userLastChange`, `lastChangeDate`). Antes de esse endpoint
 * existir nenhuma resposta de parcela devolvia autoria, e a seção era exibida
 * vazia e marcada como indisponível.
 *
 * Campo sem valor aparece como traço, e não em branco: a parcela pode nunca ter
 * sido alterada depois de criada, e nesse caso não há o que mostrar em
 * "atualizado por".
 */
export function ParcelAuditSection({
  createdBy,
  createdAt,
  updatedBy,
  updatedAt,
}: ParcelAuditSectionProps) {
  const { t } = useTranslation('individualPayment');

  const text = (value: string | null | undefined) =>
    value === null || value === undefined || value === '' ? EMPTY_VALUE : value;

  return (
    <PlanSectionCard title={t('parcelEdit.audit.title')} icon={UserCircleIcon}>
      <div className="flex flex-wrap gap-xxxs">
        <div className="flex flex-1 basis-56">
          <PlanReadOnlyField label={t('parcelEdit.audit.createdBy')} value={text(createdBy)} />
        </div>
        <div className="flex flex-1 basis-56">
          <PlanReadOnlyField
            label={t('parcelEdit.audit.createdAt')}
            value={formatDateTime(createdAt)}
          />
        </div>
        <div className="flex flex-1 basis-56">
          <PlanReadOnlyField label={t('parcelEdit.audit.updatedBy')} value={text(updatedBy)} />
        </div>
        <div className="flex flex-1 basis-56">
          <PlanReadOnlyField
            label={t('parcelEdit.audit.updatedAt')}
            value={formatDateTime(updatedAt)}
          />
        </div>
      </div>
    </PlanSectionCard>
  );
}
